import { useEffect, useState } from 'react';
import { api } from '../api';
import { EmptyState, LoadingPanel, PageHeader } from '../ui/bits';

/**
 * Intelligence (DESIGN §11.1): the agent-quality dashboard — is each agent
 * earning its keep? Approval rate among decided items, how much humans had
 * to fix (edit rate + mean edit distance), and cost per approved item.
 */

interface AgentQualityRow {
  agentKey: string;
  runs: number;
  succeeded: number;
  failed: number;
  guardrailStops: number;
  llmCostCents: number;
  itemsGenerated: number;
  approved: number;
  rejected: number;
  pending: number;
  approvalRate: number | null;
  editedRate: number | null;
  meanEditDistance: number | null;
  costPerApprovedCents: number | null;
}

const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const cents = (v: number | null) => (v === null ? '—' : `${v.toFixed(2)}¢`);
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function approvalColor(rate: number | null) {
  if (rate === null) return undefined;
  if (rate < 0.5) return 'var(--danger)';
  if (rate < 0.7) return 'var(--warn)';
  return 'var(--ok)';
}

function AgentCard({ a }: { a: AgentQualityRow }) {
  const decided = a.approved + a.rejected;
  const color = approvalColor(a.approvalRate);
  return (
    <div className="panel">
      <h3 style={{ marginBottom: 2 }}>{a.agentKey.replaceAll('_', ' ')}</h3>
      <div className="row" style={{ gap: 22, margin: '12px 0 4px' }}>
        <div className="stat" title="approved ÷ decided items">
          <div className="stat-num" style={{ color }}>
            {pct(a.approvalRate)}
          </div>
          <div className="stat-lbl">approval</div>
        </div>
        <div className="stat" title="items a human edited before deciding">
          <div className="stat-num">{pct(a.editedRate)}</div>
          <div className="stat-lbl">edited</div>
        </div>
        <div className="stat" title="0 = published untouched · 1 = fully rewritten">
          <div className="stat-num">{a.meanEditDistance === null ? '—' : a.meanEditDistance.toFixed(2)}</div>
          <div className="stat-lbl">edit distance</div>
        </div>
        <div className="stat" title="total agent cost ÷ approved items">
          <div className="stat-num">{cents(a.costPerApprovedCents)}</div>
          <div className="stat-lbl">per approved</div>
        </div>
      </div>
      {a.approvalRate !== null && (
        <div className="meter" role="presentation">
          <div className="meter-fill" style={{ width: `${Math.round(a.approvalRate * 100)}%`, background: color }} />
        </div>
      )}
      <p className="muted small" style={{ margin: '10px 0 2px' }}>
        {plural(a.runs, 'run')}: {a.succeeded} succeeded · {a.failed} failed · {a.guardrailStops} guardrail{' '}
        {a.guardrailStops === 1 ? 'stop' : 'stops'} · LLM cost {cents(a.llmCostCents)}
      </p>
      <p className="muted small" style={{ margin: 0 }}>
        {plural(a.itemsGenerated, 'item')} generated: {a.approved} approved · {a.rejected} rejected · {a.pending}{' '}
        pending{decided === 0 ? ' · nothing decided yet' : ''}
      </p>
    </div>
  );
}

export function Intelligence() {
  const [days, setDays] = useState(30);
  const [agents, setAgents] = useState<AgentQualityRow[] | null>(null);

  useEffect(() => {
    api
      .get<{ agents: AgentQualityRow[] }>(`/api/admin/agent-quality?days=${days}`)
      .then((r) => setAgents(r.agents))
      .catch(console.error);
  }, [days]);

  if (agents === null) return <LoadingPanel />;
  return (
    <div className="page-stack">
      <PageHeader
        title="Intelligence"
        sub="Is each agent earning its keep?"
        actions={
          <select value={days} aria-label="Window" onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>last 7 days</option>
            <option value={30}>last 30 days</option>
            <option value={90}>last 90 days</option>
          </select>
        }
      />
      {agents.length === 0 && (
        <div className="panel">
          <EmptyState title="No agent activity in this window" hint="Try a wider window." />
        </div>
      )}
      {agents.length > 0 && (
        <div className="card-grid">
          {agents.map((a) => (
            <AgentCard key={a.agentKey} a={a} />
          ))}
        </div>
      )}
      <p className="muted small">
        Approval counts only decided items (approved ÷ approved+rejected). Edit distance is the average share of the
        text humans changed before approving — the flywheel's job is to push it toward zero.
      </p>
    </div>
  );
}
